import { useEffect, useMemo } from 'react';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { isStatusCodeError } from '@confluence/error-types/entry-points/isStatusCodeError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

import type {
	useTrackTitleRedactionRenderedRedactionsCountQuery as useTrackTitleRedactionRenderedRedactionsCountQuery$data,
	useTrackTitleRedactionRenderedRedactionsCountQueryVariables as useTrackTitleRedactionRenderedRedactionsCountQuery$variables,
} from './__types__/useTrackTitleRedactionRenderedRedactionsCountQuery.ts';
import { useRedactionsTourActions } from './useRedactionsTourState.ts';

export const TITLE_REDACTION_STRING = '████';
const TITLE_REDACTION_WITH_ID_PATTERN = /██\s*\[ID:[^\]]+\]/u;

const doesTitleContainCurrentRedactionString = (title?: string): boolean =>
	Boolean(title && TITLE_REDACTION_WITH_ID_PATTERN.test(title));

const doesTitleContainLegacyRedactionString = (title?: string): boolean =>
	Boolean(title && title.includes(TITLE_REDACTION_STRING));

export const doesTitleContainRedactionString = (title?: string): boolean => {
	return (
		doesTitleContainLegacyRedactionString(title) || doesTitleContainCurrentRedactionString(title)
	);
};

type UseTrackTitleRedactionRenderedProps = {
	contentId: string;
	title?: string;
};

export const useTrackTitleRedactionRendered = ({
	contentId,
	title,
}: UseTrackTitleRedactionRenderedProps): void => {
	const { clearTitleRedactionRenderedForContentId, setTitleRedactionRenderedForContentId } =
		useRedactionsTourActions();

	const titleHasCurrentRedaction = useMemo(
		() => doesTitleContainCurrentRedactionString(title),
		[title],
	);
	const titleMaybeHasLegacyRedaction = useMemo(
		() => !titleHasCurrentRedaction && doesTitleContainLegacyRedactionString(title),
		[title, titleHasCurrentRedaction],
	);
	const { data, error: redactionsCountError } = useQueryInterop<
		useTrackTitleRedactionRenderedRedactionsCountQuery$data,
		useTrackTitleRedactionRenderedRedactionsCountQuery$variables
	>(
		graphql`
			query useTrackTitleRedactionRenderedRedactionsCountQuery($contentId: ID!) {
				singleContent(id: $contentId) {
					id
					redactionMetadataCount
				}
			}
		`,
		{
			skip: !titleMaybeHasLegacyRedaction,
			variables: {
				contentId,
			},
			errorPolicy: 'all',
		},
	);

	// handle 4xx errors
	useEffect(() => {
		if (redactionsCountError && isStatusCodeError(redactionsCountError, '404')) {
			markErrorAsHandled(redactionsCountError);
		}
	}, [redactionsCountError]);

	const redactionsCount = data?.singleContent?.redactionMetadataCount ?? 0;
	const isTitleRedactionConfirmed =
		titleHasCurrentRedaction || (titleMaybeHasLegacyRedaction && redactionsCount > 0);

	useEffect(() => {
		if (contentId && isTitleRedactionConfirmed) {
			setTitleRedactionRenderedForContentId(contentId);
		}
		return () => {
			clearTitleRedactionRenderedForContentId(contentId);
		};
	}, [
		clearTitleRedactionRenderedForContentId,
		contentId,
		isTitleRedactionConfirmed,
		setTitleRedactionRenderedForContentId,
	]);
};
